/**
 * `**bold**` and `*italic*` inside a line of an answer.
 *
 * The models this platform talks to emphasise constantly — the prompts themselves are written with
 * `**` — and until now it rendered as literal asterisks: an answer reading
 * `**Understand the Procedure**` looks like the product forgot to finish something.
 *
 * ## Why it takes a render prop
 *
 * The other thing that happens inside a line is citation markers, and both have to apply to the
 * *same* text: a bold sentence can carry a `[1]`, and a marker can sit inside emphasis. So this
 * splits the line into emphasised and plain runs and hands each run back to the caller, which
 * turns markers into chips. Nesting it the other way — markers first, emphasis inside — would
 * mean re-parsing the chip boundaries, which is how `**[1]**` ends up as a literal asterisk beside
 * a chip.
 *
 * **Nothing here renders HTML**, exactly as in `AnswerBody`: the runs are inserted as text and the
 * only thing this file decides is which element wraps them.
 */

import { Fragment, type ReactNode } from "react";

export interface EmphasisProps {
  text: string;
  /** Renders one run of plain text — in practice, resolving its `[n]` markers. */
  children: (run: string) => ReactNode;
}

/**
 * `**bold**` or `*italic*`, non-greedy, never across a line.
 *
 * `**` is tried first so the bold form is not read as an empty italic. A lone or unmatched asterisk
 * does not match and stays as written, which is right: a fee of `*` is not emphasis, and a model
 * that opens `**` and never closes it should not silently bold the rest of the answer.
 */
const EMPHASIS = /\*\*(.+?)\*\*|\*(.+?)\*/g;

export function Emphasis({ text, children }: EmphasisProps): JSX.Element {
  const parts: ReactNode[] = [];
  let cursor = 0;

  for (const match of text.matchAll(EMPHASIS)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      parts.push(
        <Fragment key={`text-${cursor}`}>{children(text.slice(cursor, start))}</Fragment>,
      );
    }

    const bold = match[1];
    parts.push(
      bold !== undefined ? (
        <strong key={`b-${start}`} className="font-semibold">
          {children(bold)}
        </strong>
      ) : (
        <em key={`i-${start}`}>{children(match[2]!)}</em>
      ),
    );
    cursor = start + match[0].length;
  }

  if (cursor < text.length) {
    parts.push(<Fragment key={`text-${cursor}`}>{children(text.slice(cursor))}</Fragment>);
  }

  return <>{parts}</>;
}
